import {
  Component,
  OnInit,
  input,
  InputSignal,
  effect,
  OnDestroy,
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { ManagementListRowComponent } from './management-list-row.component';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

export type ManagementListItem = {
  name: string;
  isDefault: boolean;
  key: string;
  canDelete: boolean;
};

/**
 * This component is used to manage FilterProfiles by renaming,
 * deleting or setting them as default.
 */
@Component({
  selector: 'rdo-management-list',
  template: `
    @if (config() && config().length) {
      <form class="form" (ngSubmit)="onSubmit()">
        <div class="headings-container">
          <div class="name-container heading">
            {{ columnHeaders()[0] || '' }}
          </div>
          <div class="actions-container heading">
            <span>{{ columnHeaders()[1] || '' }}</span>
            <span>{{ columnHeaders()[2] || '' }}</span>
          </div>
        </div>
        @for (item of config(); track item; let i = $index) {
          <rdo-management-list-row
            [data]="item"
            [form]="form()"
            [formKey]="item.key"
          />
        }
      </form>
    }
  `,
  styles: `
    :host {
      width: calc(100% - 7px);
      overflow-y: scroll;
      overflow-x: hidden;
      height: 100%;
      padding-right: 7px;
    }
    .headings-container {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: space-between;
      gap: 30px;
    }
    .name-container {
      font-size: 1.6rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex: 1;
      min-width: 0;
    }
    /* TODO: This width should not be hard-coded. */
    .actions-container {
      display: flex;
      align-items: center;
      font-weight: bold;
      font-size: 1.5rem;
      flex-shrink: 0;
      justify-content: space-between;
      width: 90px;
    }
  `,
  standalone: true,
  imports: [ManagementListRowComponent, ReactiveFormsModule, FormsModule],
  providers: [TranslateService],
})
export class ManagementListComponent implements OnInit, OnDestroy {
  public noneSelectedText: string = '';
  public config: InputSignal<ManagementListItem[] | null> = input(null);
  public form: InputSignal<FormGroup> = input(new FormGroup({}));
  public columnHeaders: InputSignal<string[]> = input([]);

  private formSubscription: Subscription | null = null;
  private previousDefaultKeys = new Set<string>();

  constructor(public translateService: TranslateService) {
    // Watch for config changes to setup form subscriptions
    effect(() => {
      const items = this.config();
      const formGroup = this.form();

      if (items && formGroup) {
        // Clean up previous subscription
        this.formSubscription?.unsubscribe();

        // Wait a bit for child components to register their controls
        setTimeout(() => {
          this.setupFormWatcher();
        }, 0);
      }
    });
  }

  private setupFormWatcher(): void {
    const formGroup = this.form();

    // Subscribe to form value changes
    this.formSubscription = formGroup.valueChanges.subscribe(() => {
      this.enforceOnlyOneDefault();
    });
  }

  private enforceOnlyOneDefault(): void {
    const formGroup = this.form();
    const items = this.config();

    if (!items || !formGroup) {
      return;
    }

    // Find all controls that have isDefault = true
    const currentDefaultKeys = new Set<string>();

    for (const item of items) {
      const control = formGroup.get(item.key);
      if (control && control.get('isDefault')?.value === true) {
        currentDefaultKeys.add(item.key);
      }
    }

    console.log(
      'enforceOnlyOneDefault - current:',
      Array.from(currentDefaultKeys)
    );
    console.log(
      'enforceOnlyOneDefault - previous:',
      Array.from(this.previousDefaultKeys)
    );

    // If multiple items are checked, find the newly checked one
    if (currentDefaultKeys.size > 1) {
      // Find which key was just added (newly checked)
      let newlyCheckedKey: string | null = null;

      for (const key of currentDefaultKeys) {
        if (!this.previousDefaultKeys.has(key)) {
          newlyCheckedKey = key;
          break;
        }
      }

      console.log('enforceOnlyOneDefault - newly checked:', newlyCheckedKey);

      // If we found the newly checked one, uncheck all others
      if (newlyCheckedKey) {
        for (const item of items) {
          const control = formGroup.get(item.key);
          if (
            control &&
            item.key !== newlyCheckedKey &&
            control.get('isDefault')?.value === true
          ) {
            console.log('enforceOnlyOneDefault - unchecking:', item.key);
            control.get('isDefault')?.setValue(false, { emitEvent: false });
            control.markAsDirty();
          }
        }
        // Update previous state to only include the new default
        this.previousDefaultKeys.clear();
        this.previousDefaultKeys.add(newlyCheckedKey);

        // Mark the newly checked one as dirty too
        const newlyCheckedControl = formGroup.get(newlyCheckedKey);
        if (newlyCheckedControl) {
          newlyCheckedControl.markAsDirty();
        }
      }
    } else {
      // Update previous state
      this.previousDefaultKeys = new Set(currentDefaultKeys);
    }
  }

  private updateDefaultProfileName() {
    this.noneSelectedText = this.translateService.instant(
      'main.filters.profiles.list.no_selected'
    );
  }

  ngOnInit(): void {
    console.log('management-list.component.ngOnInit  ', this.config());
  }

  ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
  }

  /**
   * Calls the Python API to delete a list of FilterProfiles which
   * ids are stoed in idsToDelete and also calls the API to persist
   * profile modifications into the DB.
   */
  public onSubmit() {
    console.log('management-list.component.onSubmit  ');
  }
}
